import React, { useCallback, useEffect, useState } from 'react';
import { GAP_STEP } from '../../../utils/overlayStack';

interface GapControlsProps {
  /** The visible space below this element, reference px (what the user sees). */
  value: number;
  onChange: (next: number) => void;
  step?: number;
  /** Floor; 0 = the boxes touch. Set elements can never overlap. */
  min?: number;
  disabled?: boolean;
}

/**
 * The one vertical control a set element has: the space below it to the next
 * element in its stack (or the safe edge). Same shape as the font-size
 * control: an editable number with stacked ▲ / ▼. ▲ = more space (the number
 * goes up; in a bottom stack the element moves up), ▼ = less space.
 * Replaces the old vertical offset, which was a translate and let elements
 * cover each other.
 */
const GapControls: React.FC<GapControlsProps> = ({ value, onChange, step = GAP_STEP, min = 0, disabled = false }) => {
  const shown = Math.max(min, Math.round(value));
  const [draft, setDraft] = useState<string>(String(shown));
  useEffect(() => { setDraft(String(shown)); }, [shown]);

  const commit = useCallback(() => {
    const parsed = parseInt(draft, 10);
    if (Number.isFinite(parsed)) {
      const next = Math.max(min, parsed);
      if (next !== shown) onChange(next);
      setDraft(String(next));
    } else {
      setDraft(String(shown));
    }
  }, [draft, shown, min, onChange]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowUp') { e.preventDefault(); onChange(shown + step); }
    else if (e.key === 'ArrowDown') { e.preventDefault(); onChange(Math.max(min, shown - step)); }
    else if (e.key === 'Enter') { e.preventDefault(); (e.target as HTMLInputElement).blur(); }
  };

  const title = `Space below: ${shown}px`;
  return (
    <div className="flex items-stretch" onMouseDown={(e) => e.stopPropagation()} title={title} data-overlay-gap={shown}>
      <input
        type="text"
        inputMode="numeric"
        value={draft}
        onChange={(e) => setDraft(e.target.value.replace(/[^0-9]/g, ''))}
        onBlur={commit}
        onKeyDown={handleKeyDown}
        onClick={(e) => e.stopPropagation()}
        disabled={disabled}
        aria-label="Space below"
        className="w-7 h-6 text-[11px] leading-none text-center bg-black/40 text-white rounded-l border border-r-0 border-white/20 focus:outline-none focus:border-white/60"
        style={{ paddingInline: 0 }}
      />
      <div className="flex flex-col">
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onChange(shown + step); }}
          disabled={disabled}
          aria-label="More space below"
          className="w-4 h-3 leading-none text-[9px] text-white/80 hover:text-white bg-black/30 hover:bg-black/50 rounded-tr border border-white/20 flex items-center justify-center disabled:opacity-40"
        >
          ▲
        </button>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onChange(Math.max(min, shown - step)); }}
          disabled={disabled || shown <= min}
          aria-label="Less space below"
          className="w-4 h-3 leading-none text-[9px] text-white/80 hover:text-white bg-black/30 hover:bg-black/50 rounded-br border border-white/20 border-t-0 flex items-center justify-center disabled:opacity-40"
        >
          ▼
        </button>
      </div>
    </div>
  );
};

export default GapControls;
